import { Bundle, BundleEntry, Questionnaire } from "../types/fhir";
import { apiClientService, ApiGateWayResponse } from "./apiClientService";
import { getNextUrl } from "./paging";
import { QuestionnaireAndResponseDto, QuestionnaireItem } from "../types/QuestionnaireItem";
import { emptyPropertyReplacer } from "../helpers/emptyPropertyReplacer";
import { SimpleQuestionnaire } from "../types/SimpleQuestionnaire";

const axiosInstance = apiClientService.axiosClient;
const apiGatewayInstance = apiClientService.apiGateway;

// CRUD operations

const getAll = async (): Promise<Questionnaire[] | undefined> => {
  let response = await axiosInstance.get<Bundle>("/fhir/Questionnaire");

  const questionnaires = new Set<Questionnaire>();

  mapBundleToQuestionnaires(response.data)?.forEach((questionnaire) => {
    questionnaires.add(questionnaire);
  });

  while (getNextUrl(response.data)) {
    const nextUrl = getNextUrl(response.data);
    if (!nextUrl) break;

    response = await axiosInstance.get<Bundle>(nextUrl);
    mapBundleToQuestionnaires(response.data)?.forEach((questionnaire) => {
      questionnaires.add(questionnaire);
    });
  }
  return Array.from(questionnaires);
};

const getAllForOverview = async (): Promise<SimpleQuestionnaire[]> => {
  const httpResponse = await apiGatewayInstance.get(`api/Questionnaire/GetAllQuestionnaires`);

  const response = httpResponse.data as ApiGateWayResponse<SimpleQuestionnaire[]>;
  if (response.error || response.data === undefined) {
    throw new Error(response.error?.message ?? "An error occurred getting Questionnaires for overview");
  }

  return response.data;
};

const getOne = async (id: string): Promise<Questionnaire | undefined> => {
  const response = await axiosInstance.get<Questionnaire>(`/fhir/Questionnaire/${id}`);
  return response.data;
};

const getQuestionnairesUsingMedia = async (mediaId: string): Promise<Questionnaire[] | undefined> => {
  const response = await axiosInstance.get<Bundle>(
    `/fhir/Questionnaire?_elements=name,url,item.extension,item.item.extension,item.item.item.extension,item.item.item.item.extension,item.item.item.item.item.extension,item.item.item.item.item.item.extension,item.item.item.item.item.item.item.extension,item.item.item.item.item.item.item.item.extension,item.item.item.item.item.item.item.item.item.extension,item.item.item.item.item.item.item.item.item.item.extension`
  );
  const questionnaires = mapBundleToQuestionnaires(response.data);
  const filteredQs: Questionnaire[] = [];
  const searchAndAdd = (
    questionnaire: Questionnaire,
    item: QuestionnaireItem[],
    mediaId: string,
    filteredQs: Questionnaire[]
  ) => {
    item.forEach((i) => {
      if (i.extension?.some((ext) => ext.url === "question-helpImage" && ext.valueString?.includes(mediaId))) {
        filteredQs.push(questionnaire);
      }
      if (i.item) {
        searchAndAdd(questionnaire, i.item, mediaId, filteredQs);
      }
    });
  };

  questionnaires?.forEach((q) => {
    searchAndAdd(q ?? "NO_ID_FOUND", q.item ?? [], mediaId, filteredQs);
  });
  return filteredQs;
};

const getByTitle = async (title: string, exact = false): Promise<Questionnaire[] | undefined> => {
  try {
    const response = await apiGatewayInstance.get<Questionnaire[]>(`api/Questionnaire/GetQuestionnairesByTitle?title=${title}`);
    if(response.status !== 200) {
      console.error(`Error fetching questionnaires by title: ${response.statusText}`);
      return undefined;
    }

    if (exact) {
      return response.data?.filter((q) => q.title === title);
    }
    return response.data;
  } catch (error) {
    console.error("Error fetching questionnaires by title:", error);
    return undefined;
  }
};

const getByUuid = async (uuid: string | undefined): Promise<Questionnaire | undefined> => {
  if (!uuid) return undefined;
  const httpResponse = await apiGatewayInstance.get(`api/Questionnaire/GetQuestionnaire?questionnaireUuid=${uuid}`);
  if(httpResponse.status === 417) {
    return undefined; // No questionnaire found with the given UUID
  }
  const response = httpResponse.data as ApiGateWayResponse<QuestionnaireAndResponseDto>;
  if(response.error || response.data === undefined) {
    throw new Error(response.error?.message ?? "An error occurred getting Questionnaire by UUID");
  }
  return response.data.questionnaire;
};

const removeOne = async (id: string): Promise<number> => {
  const response = await axiosInstance.delete<Questionnaire>(`/fhir/Questionnaire/${id}`);
  return response.status;
};

const updateQuestionnaire = async (questionnaire: Questionnaire, shouldBumpVersion: boolean, majorChange?: boolean): Promise<Questionnaire> => {
  const httpResponse = await apiGatewayInstance.put(
    `api/Questionnaire/UpdateQuestionnaire`,
    JSON.stringify({
      questionnaire,
      shouldBumpVersion: shouldBumpVersion,
      majorChange: majorChange ?? false
    }, emptyPropertyReplacer)
  );

  const response = httpResponse.data as ApiGateWayResponse<Questionnaire>;
  if (response.error || response.data === undefined) {
    throw new Error(response.error?.message ?? "An error occurred updating the Questionnaire");
  }

  return response.data;
};

const questionnaireService = {
  getAll,
  getOne,
  getByTitle,
  getByUuid,
  removeOne,
  getAllForOverview,
  getQuestionnairesUsingMedia,
  updateQuestionnaire,
};

// Mappers

const mapBundleToQuestionnaires = (bundle: Bundle): Questionnaire[] | undefined => {
  return bundle?.entry?.map((entry: BundleEntry) => {
    return JSON.parse(JSON.stringify(entry.resource));
  });
};

export default questionnaireService;
